import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { stocktakeSessionsService, sitesService } from '../services';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ArrowLeft, Loader2, Search, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, Package, FileText, BarChart3,
  Activity, Percent, CalendarDays, FileSpreadsheet,
} from 'lucide-react';
import { getApiError, formatDDMMYYYY } from '@/lib/utils';
import SitePicker from '../components/SitePicker';

const fmt = (val, decimals = 2) => {
  if (val == null || isNaN(val)) return '-';
  return Number(val).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtCurrency = (val) => {
  if (val == null || isNaN(val)) return '-';
  const n = Number(val);
  const abs = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n < 0) return <span className="text-red-600 font-medium">({'\u00A3'}{abs})</span>;
  return <span>{'\u00A3'}{abs}</span>;
};

const fmtPct = (val) => {
  if (val == null || isNaN(val)) return '-';
  return `${Number(val).toFixed(1)}%`;
};

const TXN_COLORS = {
  'Opening': 'bg-blue-50 text-blue-700',
  'Delivery': 'bg-emerald-50 text-emerald-700',
  'Credit': 'bg-violet-50 text-violet-700',
  'Sales': 'bg-rose-50 text-rose-700',
  'Wastage': 'bg-red-50 text-red-700',
  'Allowance': 'bg-purple-50 text-purple-700',
  'Management': 'bg-teal-50 text-teal-700',
  'Expected Closing': 'bg-slate-100 text-slate-700',
  'Counted Closing': 'bg-indigo-50 text-indigo-700',
};

function ProductMovementTable({ product, showVariance }) {
  const [expanded, setExpanded] = useState(false);
  const movements = product.movements || [];
  const hasMovements = movements.length > 1;
  const hasVariance = product.variance_qty != null;

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`product-row-${product.product_id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        data-testid={`product-toggle-${product.product_id}`}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-slate-900 truncate">
              {product.product_code && <span className="text-muted-foreground mr-1">{product.product_code}</span>}
              {product.product_name}
            </span>
            <Badge variant="outline" className="text-[10px] shrink-0">{product.stock_unit}</Badge>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs tabular-nums shrink-0">
          <div className="text-center w-16">
            <p className="text-muted-foreground">Opening</p>
            <p className="font-medium">{fmt(product.opening_qty)}</p>
          </div>
          <div className="text-center w-16">
            <p className="text-emerald-600 flex items-center justify-center gap-0.5"><TrendingUp className="h-3 w-3" />In</p>
            <p className="font-medium text-emerald-700">{fmt(product.total_in_qty)}</p>
          </div>
          <div className="text-center w-16">
            <p className="text-red-600 flex items-center justify-center gap-0.5"><TrendingDown className="h-3 w-3" />Out</p>
            <p className="font-medium text-red-700">{fmt(product.total_out_qty)}</p>
          </div>
          <div className="text-center w-20">
            <p className="text-muted-foreground">Expected{product.sub_unit_sales_basis && (
              <span title="Expected/Variance reflect the physical gram or ml quantity actually recorded for each sale, converted on the unit's own conversion ratio — not on any nominal or observed portion yield shown in Product Master." className="ml-0.5 text-slate-400 cursor-help">ⓘ</span>
            )}</p>
            <p className="font-semibold">{fmt(product.closing_qty, 4)}</p>
          </div>
          {showVariance && hasVariance && (
            <>
              <div className="text-center w-20">
                <p className="text-indigo-600">Counted</p>
                <p className="font-semibold text-indigo-700">{fmt(product.counted_qty, 4)}</p>
              </div>
              <div className="text-center w-20">
                <p className="text-muted-foreground">Variance</p>
                <p className={`font-semibold ${product.variance_value < 0 ? 'text-red-600' : product.variance_value > 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                  {fmtCurrency(product.variance_value)}
                </p>
              </div>
            </>
          )}
          <div className="text-center w-24">
            <p className="text-muted-foreground">WAC</p>
            <p className="font-semibold">{'\u00A3'}{fmt(product.closing_wac, 4)}</p>
          </div>
          <div className="text-center w-28">
            <p className="text-muted-foreground">Closing Value</p>
            <p className="font-semibold">{'\u00A3'}{fmt(product.closing_value)}</p>
          </div>
        </div>
      </button>

      {expanded && hasMovements && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead className="w-28">Date</TableHead>
                <TableHead className="w-28">Type</TableHead>
                <TableHead className="w-32">Reference</TableHead>
                <TableHead className="w-20 text-right">Qty In (+)</TableHead>
                <TableHead className="w-20 text-right">Qty Out (-)</TableHead>
                <TableHead className="w-24 text-right">Unit Cost</TableHead>
                <TableHead className="w-24 text-right">Value</TableHead>
                <TableHead className="w-24 text-right">Running Qty</TableHead>
                <TableHead className="w-28 text-right">Running Value</TableHead>
                <TableHead className="w-24 text-right">Running WAC</TableHead>
                <TableHead className="w-36">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movements.map((m, i) => {
                const isClosing = m.txn_type === 'Expected Closing' || m.txn_type === 'Counted Closing';
                const isCounted = m.txn_type === 'Counted Closing';
                const colorClass = TXN_COLORS[m.txn_type] || 'bg-slate-50 text-slate-600';
                return (
                  <TableRow
                    key={i}
                    className={`text-xs ${isClosing ? 'font-semibold border-t border-slate-200' : ''} ${isCounted ? 'bg-indigo-50/30' : ''}`}
                    data-testid={`movement-row-${product.product_id}-${i}`}
                  >
                    <TableCell className="font-mono text-muted-foreground">{m.date}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`text-[10px] ${colorClass}`}>{m.txn_type}</Badge>
                    </TableCell>
                    <TableCell className="truncate max-w-[130px]" title={m.reference}>{m.reference}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700 font-medium">
                      {m.qty_in != null ? fmt(m.qty_in) : ''}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-red-600 font-medium">
                      {m.qty_out != null ? fmt(m.qty_out) : ''}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {m.unit_cost_in != null ? `\u00A3${fmt(m.unit_cost_in, 4)}` : ''}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{m.value != null ? fmtCurrency(m.value) : ''}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{fmt(m.running_qty, 4)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrency(m.running_value)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{'\u00A3'}{fmt(m.running_wac, 4)}</TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-[150px]" title={m.notes}>{m.notes}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


function KpiCard({ label, value, subtitle, icon: Icon, color = 'slate', formula, formulaDetail }) {
  const [showFormula, setShowFormula] = useState(false);
  const colors = {
    slate: 'text-slate-700',
    emerald: 'text-emerald-700',
    red: 'text-red-600',
    indigo: 'text-indigo-700',
    amber: 'text-amber-700',
    blue: 'text-blue-700',
  };
  return (
    <Card
      className={`cursor-pointer transition-shadow hover:shadow-md relative ${showFormula ? 'ring-2 ring-indigo-200' : ''}`}
      onClick={() => setShowFormula(f => !f)}
      data-testid={`kpi-card-${label.replace(/[^a-zA-Z]/g, '').toLowerCase()}`}
    >
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          {Icon && <Icon className={`h-4 w-4 ${colors[color] || colors.slate}`} />}
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
        <p className={`text-lg font-bold tabular-nums ${colors[color] || colors.slate}`}>{value}</p>
        {!showFormula && subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
        {showFormula && formula && (
          <div className="mt-2 pt-2 border-t border-dashed border-slate-200 space-y-1" data-testid={`kpi-formula-${label.replace(/[^a-zA-Z]/g, '').toLowerCase()}`}>
            <p className="text-[11px] font-mono text-indigo-700">{formula}</p>
            {formulaDetail && <p className="text-[10px] text-muted-foreground">{formulaDetail}</p>}
          </div>
        )}
        {!showFormula && formula && (
          <p className="text-[9px] text-slate-400 mt-1 italic">Click for formula</p>
        )}
      </CardContent>
    </Card>
  );
}


export default function StocktakeCostingReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sessions, setSessions] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [selectedSession, setSelectedSession] = useState(searchParams.get('session_id') || '');
  const [siteFilter, setSiteFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [report, setReport] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState(null);
  const [exportingXlsx, setExportingXlsx] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [sessData, sitesData] = await Promise.all([
          stocktakeSessionsService.list({ limit: 200 }),
          sitesService.list({ limit: 500 }),
        ]);
        const items = sessData?.items || sessData?.sessions || (Array.isArray(sessData) ? sessData : []);
        setSessions(items);
        const sitesArr = Array.isArray(sitesData) ? sitesData : sitesData?.sites || sitesData?.items || [];
        setSites(sitesArr);
      } catch { /* silent */ }
      finally { setLoadingSessions(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedSession) { setReport(null); setKpis(null); return; }
    const loadReport = async () => {
      setLoadingReport(true);
      setError(null);
      try {
        const [data, kpiData] = await Promise.all([
          stocktakeSessionsService.getCostingReport(Number(selectedSession)),
          stocktakeSessionsService.getSessionKpis(Number(selectedSession)).catch(() => null),
        ]);
        setReport(data);
        setKpis(kpiData);
        setSearchParams({ session_id: selectedSession });
      } catch (err) {
        setError(getApiError(err, 'Failed to load report'));
        setReport(null);
        setKpis(null);
      } finally { setLoadingReport(false); }
    };
    loadReport();
  }, [selectedSession, setSearchParams]);

  const filteredSessions = useMemo(() => {
    if (siteFilter === 'all') return sessions;
    return sessions.filter(s => String(s.site_id) === siteFilter);
  }, [sessions, siteFilter]);

  const filteredProducts = useMemo(() => {
    if (!report?.products) return [];
    if (!searchTerm.trim()) return report.products;
    const term = searchTerm.toLowerCase();
    return report.products.filter(p =>
      (p.product_name || '').toLowerCase().includes(term) ||
      (p.product_code || '').toLowerCase().includes(term)
    );
  }, [report, searchTerm]);

  const getSiteName = (siteId) => {
    const s = sites.find(x => x.id === siteId);
    return s ? s.name : `Site ${siteId}`;
  };

  const costMethodLabel = (method) => {
    const map = {
      weighted_average: 'WAC',
      wac: 'WAC',
      fifo: 'FIFO',
      lifo: 'LIFO',
      latest_delivery: 'Last Delivery',
      standard_cost: 'Standard Cost',
    };
    return map[method] || method;
  };

  const handleExportXlsx = async () => {
    if (!selectedSession) return;
    setExportingXlsx(true);
    try {
      const response = await api.get('/stocktake/reports/costing/xlsx', {
        params: { session_id: selectedSession },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `StockAuditAnalytics_${report?.session_reference || selectedSession}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setExportingXlsx(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="costing-report-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-emerald-600" />
            Stock Audit Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Movement trail, costing analysis, variance breakdown, and KPIs
          </p>
        </div>
        {report && !loadingReport && (
          <Button
            variant="outline"
            size="sm"
            disabled={exportingXlsx}
            onClick={handleExportXlsx}
            data-testid="costing-report-export-xlsx-btn"
          >
            {exportingXlsx ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}
            Export Excel
          </Button>
        )}
      </div>

      {/* Session Selector */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Site Filter</label>
              <SitePicker
                sites={sites}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder="All Sites"
                testId="site-filter"
                showAllOption
                allLabel="All Sites"
              />
            </div>
            <div className="space-y-1.5 flex-1 min-w-[280px]">
              <label className="text-xs font-medium text-muted-foreground">Stocktake Session</label>
              <Select
                value={selectedSession}
                onValueChange={setSelectedSession}
                disabled={loadingSessions}
              >
                <SelectTrigger className="w-full h-9" data-testid="session-selector">
                  <SelectValue placeholder={loadingSessions ? 'Loading sessions...' : 'Select a session...'} />
                </SelectTrigger>
                <SelectContent>
                  {filteredSessions.map(s => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.session_reference} — {getSiteName(s.site_id)} — {formatDDMMYYYY(s.session_date)}
                      {s.session_type === 'stock_audit' ? ' (Audit)' : ' (Valuation)'}
                    </SelectItem>
                  ))}
                  {filteredSessions.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No sessions found</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading / Error */}
      {loadingReport && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Generating report...
        </CardContent></Card>
      )}
      {error && (
        <Card><CardContent className="py-6 text-center text-red-600">{error}</CardContent></Card>
      )}

      {/* Report Content */}
      {report && !loadingReport && (
        <>
          {/* Draft prior warning */}
          {report.draft_prior_warning && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="draft-prior-warning">
              <div className="shrink-0 mt-0.5 text-amber-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-800">Unposted Prior Session Detected</p>
                <p className="text-xs text-amber-700 mt-0.5">{report.draft_prior_warning}</p>
              </div>
            </div>
          )}

          {/* KPI Summary Cards */}
          {kpis && kpis.kpis && (
            <Card data-testid="kpi-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-5 w-5 text-indigo-600" />
                  Key Performance Indicators
                </CardTitle>
                <CardDescription className="text-xs">
                  {costMethodLabel(kpis.cost_method)} costing
                  {kpis.trading_days ? ` | ${kpis.trading_days} trading days` : ''}
                  {kpis.period_from ? ` | ${formatDDMMYYYY(kpis.period_from)} to ${formatDDMMYYYY(kpis.period_to)}` : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <KpiCard
                    label="COS %"
                    value={fmtPct(kpis.kpis.cos_pct)}
                    subtitle={`${'\u00A3'}${fmt(kpis.sales_at_cost)} / ${'\u00A3'}${fmt(kpis.sales_revenue)}`}
                    icon={Percent}
                    color={kpis.kpis.cos_pct > 80 ? 'red' : kpis.kpis.cos_pct > 60 ? 'amber' : 'emerald'}
                    formula="COS% = (Cost of Sales / Sales Revenue) x 100"
                    formulaDetail={`${'\u00A3'}${fmt(kpis.sales_at_cost)} / ${'\u00A3'}${fmt(kpis.sales_revenue)} = ${fmtPct(kpis.kpis.cos_pct)}. Lower is better \u2014 indicates how much of revenue is consumed by stock costs.`}
                  />
                  <KpiCard
                    label="GP %"
                    value={fmtPct(kpis.kpis.gp_pct)}
                    subtitle={`Gross Profit: ${'\u00A3'}${fmt(kpis.gross_profit)}`}
                    icon={TrendingUp}
                    color={kpis.kpis.gp_pct < 20 ? 'red' : kpis.kpis.gp_pct < 40 ? 'amber' : 'emerald'}
                    formula="GP% = ((Revenue - Cost of Sales) / Revenue) x 100"
                    formulaDetail={`(${'\u00A3'}${fmt(kpis.sales_revenue)} - ${'\u00A3'}${fmt(kpis.sales_at_cost)}) / ${'\u00A3'}${fmt(kpis.sales_revenue)} = ${fmtPct(kpis.kpis.gp_pct)}. Higher is better \u2014 profit margin after stock costs.`}
                  />
                  <KpiCard
                    label="Result"
                    value={fmtCurrency(kpis.stocktake_result)}
                    subtitle={kpis.stocktake_result >= 0 ? 'Surplus' : 'Deficit'}
                    icon={Package}
                    color={kpis.stocktake_result >= 0 ? 'emerald' : 'red'}
                    formula="Result = Actual Closing - Expected Closing"
                    formulaDetail={`${'\u00A3'}${fmt(kpis.actual_closing_value)} - ${'\u00A3'}${fmt(kpis.expected_closing_value)} = ${'\u00A3'}${fmt(kpis.stocktake_result)}. Positive = surplus stock, negative = deficit/loss.`}
                  />
                  <KpiCard
                    label="Yield %"
                    value={fmtPct(kpis.kpis.yield_pct)}
                    subtitle="Actual Receipts / (Estimated Receipts + Management) (incl VAT)"
                    icon={BarChart3}
                    color={Math.abs((kpis.kpis.yield_pct || 0) - 100) <= 2 ? 'emerald' : Math.abs((kpis.kpis.yield_pct || 0) - 100) <= 10 ? 'amber' : 'red'}
                    formula="Yield% = (Actual Receipts incl VAT / (Estimated Receipts incl VAT + Management incl VAT)) × 100"
                    formulaDetail={(() => {
                      const est = Number(kpis.estimated_receipts_incl_vat ?? kpis.estimated_receipts_excl_vat ?? 0);
                      const act = Number(kpis.accounts_sales_incl_vat ?? kpis.accounts_sales_excl_vat ?? 0);
                      const mgmt = Number(kpis.management_sales_incl_vat || 0);
                      const denom = est + mgmt;
                      return `${'\u00A3'}${fmt(act)} / (${'\u00A3'}${fmt(est)} + ${'\u00A3'}${fmt(mgmt)}) = ${'\u00A3'}${fmt(act)} / ${'\u00A3'}${fmt(denom)} = ${fmtPct(kpis.kpis.yield_pct)}. Mgmt stored negative in production, so adding naturally reduces the target by |Mgmt|. 100% = perfect match. >100% = banked MORE than reduced target (possible over-pour). <100% = banked LESS (possible loss / shrinkage).`;
                    })()}
                  />
                  <KpiCard
                    label="Stockholding Days"
                    value={kpis.kpis.stockholding_days != null ? `${kpis.kpis.stockholding_days}` : '-'}
                    subtitle="Avg stock / daily COS"
                    icon={CalendarDays}
                    color="blue"
                    formula="Days = Avg Stock Value / (COS / Trading Days)"
                    formulaDetail={`Avg Stock = (Opening ${'\u00A3'}${fmt(kpis.opening_stock_value)} + Closing ${'\u00A3'}${fmt(kpis.actual_closing_value)}) / 2. Divided by daily cost of sales over ${kpis.trading_days || '?'} trading days.`}
                  />
                  <KpiCard
                    label="Variance %"
                    value={fmtPct(kpis.kpis.variance_pct)}
                    subtitle="Result / Expected"
                    icon={Activity}
                    color={Math.abs(kpis.kpis.variance_pct || 0) <= 2 ? 'emerald' : Math.abs(kpis.kpis.variance_pct || 0) <= 5 ? 'amber' : 'red'}
                    formula="Variance% = (Result / Actual Receipts incl VAT) x 100"
                    formulaDetail={`${'\u00A3'}${fmt(kpis.stocktake_result)} / ${'\u00A3'}${fmt(kpis.expected_closing_value)} = ${fmtPct(kpis.kpis.variance_pct)}. Acceptable range: -2% to +2%.`}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stock Movement Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Card>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Opening Value</p>
                <p className="text-lg font-bold tabular-nums" data-testid="grand-opening-value">
                  {'\u00A3'}{fmt(report.grand_opening_value)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-emerald-600">Total In</p>
                <p className="text-lg font-bold tabular-nums text-emerald-700" data-testid="grand-in-value">
                  {'\u00A3'}{fmt(report.grand_in_value)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-red-600">Total Out</p>
                <p className="text-lg font-bold tabular-nums text-red-700" data-testid="grand-out-value">
                  {'\u00A3'}{fmt(report.grand_out_value)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Expected Closing</p>
                <p className="text-lg font-bold tabular-nums" data-testid="grand-closing-value">
                  {'\u00A3'}{fmt(report.grand_closing_value)}
                </p>
              </CardContent>
            </Card>
            {report.has_audit_data && (
              <>
                <Card>
                  <CardContent className="pt-4 pb-3">
                    <p className="text-xs text-indigo-600">Counted Closing</p>
                    <p className="text-lg font-bold tabular-nums text-indigo-700" data-testid="grand-counted-value">
                      {'\u00A3'}{fmt(report.grand_counted_value)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Variance</p>
                    <p className={`text-lg font-bold tabular-nums ${(report.grand_variance_value || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="grand-variance-value">
                      {fmtCurrency(report.grand_variance_value)}
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Report meta */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">{report.session_reference}</Badge>
            <span>{report.site_name}</span>
            <span>|</span>
            <span>Period: {formatDDMMYYYY(report.period_from || report.session_date)} to {formatDDMMYYYY(report.period_to || report.session_date)}</span>
            <span>|</span>
            <span>{report.product_count} products</span>
            {report.effective_cost_method && (
              <>
                <span>|</span>
                <Badge variant="secondary" className="text-[10px]">
                  {costMethodLabel(report.effective_cost_method)}
                </Badge>
              </>
            )}
          </div>

          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 h-9"
              data-testid="product-search"
            />
          </div>

          {/* Product Movement Tables */}
          <div className="space-y-2">
            {filteredProducts.length === 0 && (
              <Card><CardContent className="py-6 text-center text-muted-foreground">
                {searchTerm ? 'No products match your search' : 'No product data available'}
              </CardContent></Card>
            )}
            {filteredProducts.map(p => (
              <ProductMovementTable key={p.product_id} product={p} showVariance={report.has_audit_data} />
            ))}
          </div>
        </>
      )}

      {/* No session selected */}
      {!selectedSession && !loadingReport && (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Select a stocktake session above to generate the report</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
